import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Stack,
  InputAdornment,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Menu,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  Pagination,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Search, Visibility, Delete, FilterList, Add, WarningAmber, ArrowDropDown, ShoppingCart } from '@mui/icons-material';
import { useEffect, useState, useMemo, useCallback, useRef, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { listReceipts, deleteReceipt, SortField, SortOrder } from '@/api/receipt';
import { ReceiptSummary, ProcessingStatus } from '../../generated/receipt/v1/receipt_pb';
import { useReceiptListStore } from '@/store/receiptListStore';
import { useUploadStore } from '@/store/uploadStore';
import { normalizeStoreName } from '@/utils/textUtils';
import { PaymentIcon } from '@/components/PaymentIcon';

type OrderBy = 'date' | 'store' | 'total';

export default function ListReceiptsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [receipts, setReceipts] = useState<ReceiptSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState<ReceiptSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [addReceiptAnchorEl, setAddReceiptAnchorEl] = useState<null | HTMLElement>(null);
  const MAX_INPUT_CHARS = 500;

  // Upload store for tracking background uploads
  const { uploads, lastCompletedUploadId } = useUploadStore();
  const hasPendingUploads = Array.from(uploads.values()).some(
    (u) => u.status === 'pending' || u.status === 'uploading'
  );

  // Persisted filter state from store
  const {
    searchQuery,
    orderBy,
    order,
    storeFilter,
    showOnlyUnreconciled,
    page,
    pageSize,
    setSearchQuery,
    setOrderBy,
    setOrder,
    setStoreFilter,
    setShowOnlyUnreconciled,
    setPage,
    setPageSize,
  } = useReceiptListStore();

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const getCharLimitHelper = (value: string) =>
    value.length >= MAX_INPUT_CHARS ? `${MAX_INPUT_CHARS}/${MAX_INPUT_CHARS} characters` : undefined;

  const handleAddReceiptClick = (event: MouseEvent<HTMLElement>) => {
    setAddReceiptAnchorEl(event.currentTarget);
  };

  const handleAddReceiptClose = () => {
    (document.activeElement as HTMLElement)?.blur();
    setAddReceiptAnchorEl(null);
  };

  const handleDeleteClick = (receipt: ReceiptSummary) => {
    setReceiptToDelete(receipt);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!receiptToDelete) return;

    try {
      setIsDeleting(true);
      await deleteReceipt({ receiptId: receiptToDelete.receiptId });
      setReceipts((prev) => prev.filter((r) => r.receiptId !== receiptToDelete.receiptId));
      setDeleteDialogOpen(false);
      setReceiptToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete receipt');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReceiptToDelete(null);
  };

  // Get unique stores for filter dropdown
  const uniqueStores = useMemo(() => {
    const stores = receipts
      .map((r) => normalizeStoreName(r.store))
      .filter((s) => s && s !== 'N/A');
    return Array.from(new Set(stores)).sort();
  }, [receipts]);

  // Handle sort request
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
  };

  // Debounced search query for API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Convert frontend orderBy to proto SortField
  const getSortField = useCallback((field: OrderBy): SortField => {
    switch (field) {
      case 'date':
        return SortField.DATE;
      case 'store':
        return SortField.STORE;
      case 'total':
        return SortField.TOTAL;
      default:
        return SortField.DATE;
    }
  }, []);

  const fetchReceipts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listReceipts({
        page,
        pageSize,
        sortBy: getSortField(orderBy),
        sortOrder: order === 'asc' ? SortOrder.ASC : SortOrder.DESC,
        search: debouncedSearchQuery.trim() || undefined,
        storeFilter: storeFilter !== 'all' ? storeFilter : undefined,
        reconciliationStatusFilter: showOnlyUnreconciled ? 'needs_review' : undefined,
      });
      setReceipts(response.receipts);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load receipts');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, orderBy, order, getSortField, debouncedSearchQuery, storeFilter, showOnlyUnreconciled]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Refetch when an upload completes
  useEffect(() => {
    if (lastCompletedUploadId) {
      fetchReceipts();
    }
  }, [lastCompletedUploadId, fetchReceipts]);

  // Auto-refresh while receipts are still processing
  const hasProcessingReceipts = receipts.some(
    (r) => r.processingStatus === ProcessingStatus.PROCESSING
  );

  useEffect(() => {
    if (!hasProcessingReceipts) return;
    const interval = setInterval(fetchReceipts, 5000);
    return () => clearInterval(interval);
  }, [hasProcessingReceipts, fetchReceipts]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSize: 10 | 25 | 50 | null
  ) => {
    if (newSize !== null) {
      setPageSize(newSize); // Store automatically resets page to 1
    }
  };

  return (
    <Box sx={{ py: { xs: 2, md: 3 }, px: { xs: 3, sm: 4, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Receipts
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {hasPendingUploads && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                color: 'info.main',
              }}
            >
              <CircularProgress size={16} color="inherit" />
              <Typography variant="body2" fontWeight={500}>
                Processing...
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            endIcon={<ArrowDropDown />}
            fullWidth={isMobile}
            sx={{ borderRadius: 2, boxShadow: 'none', py: { xs: 1, md: 0.5 } }}
            onClick={handleAddReceiptClick}
            aria-controls={addReceiptAnchorEl ? 'add-receipt-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={addReceiptAnchorEl ? 'true' : undefined}
          >
            Add Receipt
          </Button>
          <Menu
            id="add-receipt-menu"
            anchorEl={addReceiptAnchorEl}
            open={Boolean(addReceiptAnchorEl)}
            onClose={handleAddReceiptClose}
            disableRestoreFocus
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem component={RouterLink} to="/receipts/new" onClick={handleAddReceiptClose}>
              Create a manual receipt
            </MenuItem>
            <MenuItem component={RouterLink} to="/upload" onClick={handleAddReceiptClose}>
              Upload a receipt image
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      {/* Filters */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 2 }, '&:last-child': { pb: { xs: 2.5, md: 2 } } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="center">
            <TextField
              size="small"
              placeholder="Search receipts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              helperText={getCharLimitHelper(searchQuery)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, flexGrow: 1 }}>
                <InputLabel>Filter by Store</InputLabel>
                <Select
                  id="store-filter"
                  value={storeFilter}
                  label="Filter by Store"
                  onChange={(e) => setStoreFilter(e.target.value)}
                  MenuProps={{ disableRestoreFocus: true }}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Stores</MenuItem>
                  <Divider />
                  {(storeFilter !== 'all' && !uniqueStores.includes(storeFilter)
                    ? [storeFilter, ...uniqueStores]
                    : uniqueStores
                  ).map((store) => (
                    <MenuItem key={store} value={store}>
                      {store}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyUnreconciled}
                    onChange={(e) => setShowOnlyUnreconciled(e.target.checked)}
                    size="small"
                    color="warning"
                  />
                }
                label="Needs review"
                sx={{ 
                  whiteSpace: 'nowrap', 
                  mr: 0,
                  ml: { xs: -0.5, sm: 0 } // Adjust margin for better alignment when stacked
                }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : receipts.length === 0 && !error ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {debouncedSearchQuery || storeFilter !== 'all' || showOnlyUnreconciled
            ? 'No receipts match your search criteria.'
            : 'No receipts found. Upload your first receipt to get started!'}
        </Alert>
      ) : (
        <>
          {/* Desktop Table View */}
          <Card
            elevation={0}
            sx={{
              display: { xs: 'none', md: 'block' },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>
                      <TableSortLabel
                        active={orderBy === 'date'}
                        direction={orderBy === 'date' ? order : 'asc'}
                        onClick={() => handleRequestSort('date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>
                      <TableSortLabel
                        active={orderBy === 'store'}
                        direction={orderBy === 'store' ? order : 'asc'}
                        onClick={() => handleRequestSort('store')}
                      >
                        Store
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Payment</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>
                      <TableSortLabel
                        active={orderBy === 'total'}
                        direction={orderBy === 'total' ? order : 'asc'}
                        onClick={() => handleRequestSort('total')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((receipt) => {
                    const isProcessing = receipt.processingStatus === ProcessingStatus.PROCESSING;
                    const isFailed = receipt.processingStatus === ProcessingStatus.FAILED;
                    return (
                    <TableRow
                      key={receipt.receiptId}
                      hover
                      onClick={() => navigate(`/receipt/${receipt.receiptId}`)}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        opacity: isProcessing ? 0.7 : 1,
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.primary" fontWeight="500">
                          {receipt.date || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {isProcessing && <CircularProgress size={14} />}
                          <Typography variant="body2" fontWeight="500">
                            {isProcessing ? 'Processing...' : isFailed ? 'Processing failed' : normalizeStoreName(receipt.store)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {(isProcessing || isFailed) ? null : receipt.payment ? <PaymentIcon paymentMethod={receipt.payment} /> :
                          <Typography variant="caption" color="text.secondary">N/A</Typography>
                        }
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        {(isProcessing || isFailed) ? null : (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          {receipt.reconciliationStatus !== 'reconciled' && (
                            <Tooltip title="Needs review - totals don't match">
                              <WarningAmber fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                          <Typography variant="body2" fontWeight="600" color="primary.main">
                            {formatCurrency(receipt.total)}
                          </Typography>
                        </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/receipt/${receipt.receiptId}`);
                              }}
                              size="small"
                              color="primary"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Create Shopping List">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/lists', { state: { fromReceiptSummary: receipt } });
                              }}
                              size="small"
                              color="primary"
                              disabled={isProcessing}
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                              }}
                            >
                              <ShoppingCart fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Receipt">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(receipt);
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Mobile Card View */}
          <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {receipts.map((receipt) => {
              const isProcessingReceipt = receipt.processingStatus === ProcessingStatus.PROCESSING;
              const isFailedReceipt = receipt.processingStatus === ProcessingStatus.FAILED;
              return (
              <Card
                key={receipt.receiptId}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  opacity: isProcessingReceipt ? 0.7 : 1,
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Stack spacing={2.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0, mr: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {isProcessingReceipt && <CircularProgress size={14} />}
                          <Typography variant="subtitle1" fontWeight="700" noWrap sx={{ lineHeight: 1.2, mb: 0.5 }}>
                            {isProcessingReceipt ? 'Processing...' : isFailedReceipt ? 'Processing failed' : normalizeStoreName(receipt.store)}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {receipt.date || 'No Date'}
                        </Typography>
                      </Box>
                      {!isProcessingReceipt && !isFailedReceipt && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {receipt.reconciliationStatus !== 'reconciled' && (
                          <Tooltip title="Needs review - totals don't match">
                            <WarningAmber fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                        <Typography variant="h6" fontWeight="700" color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                          {formatCurrency(receipt.total)}
                        </Typography>
                      </Stack>
                      )}
                    </Box>

                    <Divider />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">PAYMENT</Typography>
                        {(isProcessingReceipt || isFailedReceipt) ? null : receipt.payment ?
                          <PaymentIcon paymentMethod={receipt.payment} /> :
                          <Typography variant="caption" color="text.disabled">N/A</Typography>
                        }
                      </Box>
                      <Stack direction="row" spacing={1.5}>
                        <Button
                          component={RouterLink}
                          to={`/receipt/${receipt.receiptId}`}
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          sx={{ borderRadius: 2, px: 2 }}
                        >
                          View
                        </Button>
                        <Tooltip title="Create Shopping List">
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={isProcessingReceipt}
                            onClick={() => navigate('/lists', { state: { fromReceiptSummary: receipt } })}
                            sx={{
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                            }}
                          >
                            <ShoppingCart fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(receipt)}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.error.main, 0.02),
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              );
            })}
          </Stack>

          {/* Pagination Controls */}
          <Card
            elevation={0}
            sx={{
              mt: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Per page:
                  </Typography>
                  <ToggleButtonGroup
                    value={pageSize}
                    exclusive
                    onChange={handlePageSizeChange}
                    size="small"
                  >
                    <ToggleButton value={10}>10</ToggleButton>
                    <ToggleButton value={25}>25</ToggleButton>
                    <ToggleButton value={50}>50</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  alignItems="center"
                  sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {totalCount > 0
                      ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`
                      : '0 receipts'}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Receipt?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the receipt from 
            <Box component="span" fontWeight="600" color="text.primary"> {receiptToDelete?.store && normalizeStoreName(receiptToDelete.store)} </Box>
            dated {receiptToDelete?.date}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteCancel} 
            disabled={isDeleting}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
            sx={{ borderRadius: 2, boxShadow: 'none' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
